import update from 'immutability-helper';
import type { StyleSpecification as MapStyle } from 'mapbox-gl';
import { useEffect, useState } from 'react';
import { MAP_STYLES } from 'src/lib/player/constants';
import type { ExternalLayerType } from 'src/lib/player/store';

// From https://github.com/mapbox/mapbox-gl-language/issues/31#issuecomment-510307581
function fixLanguage(layers: Array<any>, language: string) {
  const labelList = layers.filter((layer) => {
    return /-label/.test(layer.id);
  });
  for (const labelLayer of labelList) {
    labelLayer.layout['text-field'] = ['coalesce', ['get', `name_${language}`], ['get', 'name']];
  }
}

function getMapStyleKey(mapType: string) {
  const mapStyleInfo = MAP_STYLES.find((info) => info.label === mapType);
  return mapStyleInfo != null ? mapStyleInfo.key : MAP_STYLES[0].key;
}

const mapStyleCache = new Map<string, MapStyle>();

async function loadMapStyle(mapStyleKey: string): Promise<MapStyle> {
  const res = await fetch(
    `https://api.mapbox.com/styles/v1/mapbox/${mapStyleKey}?optimize=true&access_token=${import.meta.env.VITE_FRONTEND_MAPBOX_ACCESS_TOKEN}`,
  );
  const mapStyleData = await res.json();
  fixLanguage(mapStyleData.layers, 'mul');
  return mapStyleData;
}

async function loadMapStyleMemoized(mapStyleKey: string): Promise<MapStyle> {
  const storedMapStyle = mapStyleCache.get(mapStyleKey);
  if (storedMapStyle != null) {
    return storedMapStyle;
  }

  const promise = await loadMapStyle(mapStyleKey);
  mapStyleCache.set(mapStyleKey, promise);
  return promise;
}

function drawExternalLayers(mapStyle: MapStyle, externalLayers: Array<ExternalLayerType>) {
  let newMapStyle: MapStyle = mapStyle;

  for (const externalLayer of externalLayers) {
    const featureId = externalLayer.id;

    const layer = {
      id: featureId,
      type: 'raster',
      source: featureId,
      paint: {},
    };

    const source = {
      type: 'raster',
      tiles: externalLayer.tiles,
      tileSize: externalLayer.tileSize,
    };
    newMapStyle = update(newMapStyle as any, {
      layers: { $push: [layer] },
      sources: {
        [featureId]: {
          $set: source,
        },
      },
    }) as MapStyle;
  }
  return newMapStyle;
}
export function useMapStyle(mapType: string, externalLayers: Array<ExternalLayerType>) {
  const [mapStyle, setMapStyle] = useState<MapStyle | null>(null);

  useEffect(() => {
    (async () => {
      let mapStyleData = await loadMapStyleMemoized(getMapStyleKey(mapType));
      mapStyleData = drawExternalLayers(mapStyleData, externalLayers);
      setMapStyle(mapStyleData);
    })();
  }, [mapType, externalLayers]);

  return mapStyle;
}
